import React from 'react';
import {
  DOCUMENT_VALIDITY_MONTHS,
  getDocumentValidity,
  getDocumentValidityLabel
} from '../../utils/documentExpiry';

/**
 * Shows whether a document is still current.
 *
 * Where the API has judged the document by its type's validity rule (validityStatus / validUntil), that is
 * shown: a proof of address is measured from its own date, a TCS PIN from its SARS issue date, an ID until the
 * expiry on it, and transaction documents belong to their application. Screens that do not pass it yet fall
 * back to the old flat three months from upload.
 *
 * Expiry is advisory on an application: it tells an officer that a document should be refreshed, but never
 * stops them proceeding.
 */

interface DocumentValidityBadgeProps {
  /** The document's upload date (fallback rule only). */
  uploadDate?: string | null;
  /** CURRENT, EXPIRING_SOON, EXPIRED or VERIFICATION_REQUIRED, from the API. */
  status?: string | null;
  validUntil?: string | null;
  /** The type's validity rule in words, for the tooltip. */
  rule?: string | null;
}

const formatDay = (value: string | Date) =>
  new Date(value).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

const daysUntil = (value: string) => {
  const end = new Date(value);
  const today = new Date();
  const utc = (d: Date) => Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  return Math.round((utc(end) - utc(today)) / 86_400_000);
};

const BADGE = {
  green: 'bg-success-subtle text-success-emphasis',
  amber: 'bg-warning-subtle text-warning-emphasis',
  red: 'bg-danger-subtle text-danger-emphasis',
  grey: 'bg-secondary-subtle text-secondary-emphasis',
};

const DocumentValidityBadge: React.FC<DocumentValidityBadgeProps> = ({ uploadDate, status, validUntil, rule }) => {
  if (status) {
    let label: string;
    let variant: string;
    let icon: string;
    switch (status) {
      case 'EXPIRED':
        label = 'Expired: replacement required';
        variant = BADGE.red;
        icon = 'fa-exclamation-triangle';
        break;
      case 'EXPIRING_SOON': {
        const days = validUntil ? daysUntil(validUntil) : null;
        label = days === null ? 'Expiring soon' : days <= 0 ? 'Expires today' : `Expires in ${days} day${days === 1 ? '' : 's'}`;
        variant = BADGE.amber;
        icon = 'fa-clock-o';
        break;
      }
      case 'VERIFICATION_REQUIRED':
        label = 'Verification required';
        variant = BADGE.amber;
        icon = 'fa-question-circle';
        break;
      default:
        label = 'Current';
        variant = BADGE.green;
        icon = 'fa-check';
    }
    const title = [
      rule || '',
      validUntil ? `${status === 'EXPIRED' ? 'Expired' : 'Valid until'} ${formatDay(validUntil)}` : '',
      status === 'VERIFICATION_REQUIRED' ? 'The document date this rule needs was not captured' : '',
    ].filter(Boolean).join('. ');
    return (
      <span className={`badge rounded-pill ${variant}`} title={title || undefined}>
        <i className={`fa ${icon} me-1`}></i>
        {label}
      </span>
    );
  }

  const validity = getDocumentValidity(uploadDate);

  // Without an upload date there is nothing to measure against, so say nothing rather than guess.
  if (!validity) return <span className="text-muted">&mdash;</span>;

  const expiryDate = formatDay(validity.expiresAt);

  const variant = validity.isExpired ? BADGE.red : validity.isExpiringSoon ? BADGE.amber : BADGE.green;
  const icon = validity.isExpired ? 'fa-exclamation-triangle' : validity.isExpiringSoon ? 'fa-clock-o' : 'fa-check';

  return (
    <span
      className={`badge rounded-pill ${variant}`}
      title={validity.isExpired
        ? `Valid for ${DOCUMENT_VALIDITY_MONTHS} months from upload; expired on ${expiryDate}`
        : `Valid until ${expiryDate}`}
    >
      <i className={`fa ${icon} me-1`}></i>
      {getDocumentValidityLabel(validity)}
    </span>
  );
};

export default DocumentValidityBadge;
